import { Interface } from '@ethersproject/abi';
import { TransactionReceipt } from '@ethersproject/abstract-provider';
import { BigNumber } from '@ethersproject/bignumber';
import { Constants, Currency, Media, Network, StringUtils } from '@manifoldxyz/js-ts-utils';
import { abis } from '@manifoldxyz/typed-contracts';
import { appsApi } from '@/api/apps';
import { ClaimStudioInstance, ScribeStudioInstance } from '@/api/models/studioInstance';
import { DataClientNFT, RequiredFund } from '@/common/types';
import ClaimExtensionContract, { Claim } from '@/contracts/claimExtensionContract';
import ERC20Contract from '@/contracts/erc20Contract';
import useMainStore from '@/store/mainStore';
import { STUDIO_LINK_CAMPAIGN_AND_MEDIUM } from './constants';

/**
 * OpenSea and EIP721/EIP1155 Metadata JSON Schema
 * @see https://eips.ethereum.org/EIPS/eip-721
 * @see https://eips.ethereum.org/EIPS/eip-1155
 * @see https://docs.opensea.io/docs/metadata-standards
 * @todo move to js-ts-utils, this is a static type
 */
export type NFTMetadata = {
  name: string;
  description: string;
  image: string;
  animation_url?: string;
  properties?: Record<string, unknown>;
  external_url?: string;
};

export function isWalletConnect(): boolean {
  if (
    localStorage.getItem('connectMethod') &&
    localStorage.getItem('connectMethod') === 'walletConnect'
  ) {
    return true;
  }
  return false;
}

/**
 * @warn Only use this in the UX, do not use this to identify a network
 * @param network - the network to get the name of
 * @returns the name of the network as a string
 */
export function networkName(network: number): string {
  return StringUtils.capitalizeFirstLetter(Network.getDisplayNameForNetworkId(network));
}

export function ethCurrencyName(network: number): string {
  return Currency.ethCurrencyNameForNetwork(network);
}

export enum InstanceState {
  /** start date is in the future */
  Upcoming = 'upcoming',
  /** start date is in the past, end date is in the future/never, and instance is not sold out */
  Live = 'live',
  /** start date is in the past, end date is in the future/never, but instance has no more supply */
  SoldOut = 'sold_out',
  /** end date is in the past */
  Ended = 'ended',
  /** instance is loading */
  Loading = 'loading',
}

export function getTelemetryInstanceState(
  instance: ClaimStudioInstance | ScribeStudioInstance,
  claim: Claim,
): InstanceState {
  // no start date means it has started
  let hasStarted: boolean = true;
  // no end date means it never ends
  let hasEnded: boolean = false;
  // no total supply (0 or undefined) means it has unlimited supply
  let hasSoldOut: boolean = false;

  const now = Date.now();
  if (instance.publicData.startDate) {
    hasStarted = instance.publicData.startDate < now;
  }
  if (instance.publicData.endDate) {
    hasEnded = instance.publicData.endDate < now;
  }

  if (claim.totalMax) {
    hasSoldOut = claim.totalMax <= claim.total;
  }

  if (hasEnded) {
    return InstanceState.Ended;
  }
  // not ended yet, has it started?
  if (!hasStarted) {
    return InstanceState.Upcoming;
  }
  // not ended yet, has started. is it sold out?
  if (hasSoldOut) {
    return InstanceState.SoldOut;
  }
  // not ended yet, has started, and is not sold out
  return InstanceState.Live;
}

export function convertSVGToDataURI(raw: string): string {
  return Media.encodeSVGDataString(raw);
}

/**
 * checks if two tokens from nft analytics are the same
 * count and owner address dont matter, just address and tokenId
 */
export const twoTokensAreTheSame = (token1: DataClientNFT, token2: DataClientNFT): boolean => {
  return token1.tokenId === token2.tokenId && token1.contractAddress === token2.contractAddress;
};

export const parseMintedTokenIdsFromClaimReceiptWithLogs = (
  receipt: TransactionReceipt,
  redeemSpec: string,
  walletAddress: string,
): string[] => {
  const ids = [];
  const mintToAddress = useMainStore.getState().mintToAddress;

  if (receipt && receipt.logs) {
    for (const log of receipt.logs) {
      try {
        const theABI = redeemSpec.toUpperCase() === 'ERC721' ? abis.ERC721Abi : abis.ERC1155Abi;
        const iface = new Interface(theABI);

        const parsed = iface.parseLog(log);

        // Check if the token was sent to either the wallet address or the mintTo address
        const receiverAddress = parsed.args.to.toLowerCase();
        const isWalletReceiver = receiverAddress === walletAddress.toLowerCase();
        const isMintToReceiver = mintToAddress && receiverAddress === mintToAddress.toLowerCase();

        if (isWalletReceiver || isMintToReceiver) {
          const id = parsed.args.tokenId?.toString() || parsed.args.id?.toString();
          ids.push(id);
          break;
        }
      } catch (e) {
        // Do nothing, log will failed parse for events specific to extension contract
      }
    }
  }
  return ids;
};

// Try catch because localStorage can throw an error if the user has disabled
export function trySetLocalStorage(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // ignore
  }
}

export function tryGetLocalStorage(key: string) {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    // ignore
  }
  return '';
}

/**
 * @warn terribly written for typesafety avoid usage
 */
export function extractProxyData(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  proxy: any[][],
): string[][] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const outerArray: any[] = Array.from(proxy);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return outerArray.map((innerArray: any[]) => Array.from(innerArray) as string[]);
}

export function isUnlimitedSupply(claim: Claim): boolean {
  return claim.totalMax === 0;
}

export function pluralize(count: number, singular: string, plural?: string): string {
  if (count === 1) {
    return singular;
  } else {
    return plural || singular + 's';
  }
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function isJSON(str: string): boolean {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

/**
 * We are using the extensionAddressUpdatableMintFee721 and extensionAddressUpdatableMintFee1155 for Polygon and ApeChain specific claims.
 * Reason being this contract supports updatable mint fees, since MATIC and APE are volatile and we want to best approximate it with ETH.
 */
export function getExtensionAddress(data: ClaimStudioInstance | ScribeStudioInstance) {
  if (data.publicData.contract.spec === 'ERC721') {
    if (
      (data.publicData.network === 137 || data.publicData.network === 33139) &&
      // check and tell the type compiler this has extensionAddressUpdatableMintFee721
      (data as ClaimStudioInstance).publicData.extensionAddressUpdatableMintFee721
    ) {
      return (data as ClaimStudioInstance).publicData.extensionAddressUpdatableMintFee721.value;
    } else {
      return data.publicData.extensionAddress721.value;
    }
  }

  if (data.publicData.contract.spec === 'ERC1155') {
    if (
      (data.publicData.network === 137 || data.publicData.network === 33139) &&
      // check and tell the type compiler this has extensionAddressUpdatableMintFee1155
      (data as ClaimStudioInstance).publicData.extensionAddressUpdatableMintFee1155
    ) {
      return (data as ClaimStudioInstance).publicData.extensionAddressUpdatableMintFee1155.value;
    } else {
      return data.publicData.extensionAddress1155.value;
    }
  }

  throw new Error('Unable to get extension address: Unsupported contract spec');
}

/**
 * Recursively finds and returns all children as an array.
 * The last element of the array is the root element.
 */
export const getAllChildren = (htmlElement: Element): Element[] => {
  if (!htmlElement.children || htmlElement.children?.length === 0) {
    return [htmlElement];
  }

  const allChildElements = [];

  for (let i = 0; i < htmlElement.children.length; i++) {
    const children = getAllChildren(htmlElement.children[i]);
    if (children) allChildElements.push(...children);
  }
  allChildElements.push(htmlElement);

  return allChildElements;
};

export const toSnakeCase = (str: string): string => {
  return str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
};

/**
 * dbl check this has the right medium
 */
export const generateCreateYourOwnLink = (
  studioInstance: ClaimStudioInstance | ScribeStudioInstance,
) => {
  const utmContent = studioInstance.id ? `&utm_content=${studioInstance.id}` : '';
  const appPath = studioInstance.appId ? `apps/${studioInstance.appId}/new` : '';
  const link = `https://studio.manifold.xyz/${appPath}?utm_source=claim&${STUDIO_LINK_CAMPAIGN_AND_MEDIUM}${utmContent}`;
  return link;
};

export async function fetchClaimableMerkleInfo(
  merkleTreeId: number,
  walletAddress: string,
  contract: ClaimExtensionContract,
) {
  const merkleInfo = await appsApi.getMerkleTree(merkleTreeId, walletAddress);
  const mintIndices = merkleInfo.map((claimMerkleInfo) => claimMerkleInfo.value);
  const mintIndicesStatus = await contract.checkMintIndices(mintIndices);
  const claimableMerkleInfo = merkleInfo.filter((_, index) => !mintIndicesStatus[index]);

  return {
    mintIndices: claimableMerkleInfo.map((claimMerkleInfo) => claimMerkleInfo.value),
    merkleProofs: claimableMerkleInfo.map((claimMerkleInfo) => claimMerkleInfo.merkleProof),
  };
}

/**
 * Calculates the missing funds needed for a transaction
 * Compares the user's current ERC20 token balance against the required amount
 *
 * @param userWalletAddress - The wallet address to check the balance for
 * @param requiredFund - The fund requirement details including token address and amount
 * @returns The missing amount needed, or 0 if sufficient funds are available
 *
 * @note Only supports ERC20 tokens - returns 0 for native ETH transactions
 * @note Gracefully handles errors by assuming sufficient funds to prevent blocking transactions
 */
/**
 * Gets a URL query parameter value by name
 * @param name - The name of the query parameter to retrieve
 * @returns The parameter value if found, null otherwise
 */
export function getURLParameter(name: string): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
}

export async function getMissingFunds(
  userWalletAddress: string,
  requiredFund: RequiredFund,
): Promise<RequiredFund> {
  // we can only check for erc20 funds right now
  if (requiredFund.address === Constants.NULL_ADDRESS) {
    return {
      amount: BigNumber.from(0),
      decimals: requiredFund.decimals,
      networkId: requiredFund.networkId,
      address: requiredFund.address,
    };
  }
  // check if user has enough funds already
  const erc20 = new ERC20Contract(requiredFund.networkId, requiredFund.address);

  // assume no missing funds so we can fallback to on-chain check if errors occur
  let balance = requiredFund.amount;
  try {
    balance = await erc20.balanceOf(userWalletAddress);
  } catch (e) {}

  if (balance.gte(requiredFund.amount)) {
    return {
      amount: BigNumber.from(0),
      decimals: requiredFund.decimals,
      networkId: requiredFund.networkId,
      address: requiredFund.address,
    };
  }

  return {
    amount: requiredFund.amount.sub(balance),
    decimals: requiredFund.decimals,
    networkId: requiredFund.networkId,
    address: requiredFund.address,
  };
}
